import React from 'react';
import { Calendar, DollarSign } from 'lucide-react';
import { PeriodType } from '../../types/professional-payment';

interface PeriodSelectorProps {
  periodType: PeriodType;
  onPeriodTypeChange: (type: PeriodType) => void;
  startDate: string;
  endDate: string;
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
  month: number;
  year: number;
  onMonthChange: (month: number) => void;
  onYearChange: (year: number) => void;
}

export function PeriodSelector({
  periodType,
  onPeriodTypeChange,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  month,
  year,
  onMonthChange,
  onYearChange,
}: PeriodSelectorProps) {
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);
  const months = [
    { value: 1, label: 'Janeiro' },
    { value: 2, label: 'Fevereiro' },
    { value: 3, label: 'Março' },
    { value: 4, label: 'Abril' },
    { value: 5, label: 'Maio' },
    { value: 6, label: 'Junho' },
    { value: 7, label: 'Julho' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Setembro' },
    { value: 10, label: 'Outubro' },
    { value: 11, label: 'Novembro' },
    { value: 12, label: 'Dezembro' },
  ];

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold mb-4 text-gray-900">Tipo de Período</h3>

      <div className="space-y-4">
        <div className="flex gap-4">
          <label className="flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all hover:bg-gray-50 flex-1">
            <input
              type="radio"
              name="periodType"
              value="billing_period"
              checked={periodType === 'billing_period'}
              onChange={(e) => onPeriodTypeChange(e.target.value as PeriodType)}
              className="w-4 h-4 text-blue-600"
            />
            <div className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-blue-600" />
              <div>
                <div className="font-medium text-gray-900">Por Faturamento</div>
                <div className="text-sm text-gray-500">Baseado no fechamento mensal dos pacientes</div>
              </div>
            </div>
          </label>

          <label className="flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all hover:bg-gray-50 flex-1">
            <input
              type="radio"
              name="periodType"
              value="date_range"
              checked={periodType === 'date_range'}
              onChange={(e) => onPeriodTypeChange(e.target.value as PeriodType)}
              className="w-4 h-4 text-blue-600"
            />
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-green-600" />
              <div>
                <div className="font-medium text-gray-900">Por Data de Atendimento</div>
                <div className="text-sm text-gray-500">Período fixo de datas customizadas</div>
              </div>
            </div>
          </label>
        </div>

        {periodType === 'billing_period' ? (
          <div className="grid grid-cols-2 gap-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mês
              </label>
              <select
                value={month}
                onChange={(e) => onMonthChange(Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {months.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ano
              </label>
              <select
                value={year}
                onChange={(e) => onYearChange(Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {years.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4 p-4 bg-green-50 rounded-lg border border-green-200">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Data Início
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => onStartDateChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Data Fim
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => onEndDateChange(e.target.value)}
                min={startDate}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>

            <div className="col-span-2">
              <div className="flex gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={() => {
                    const now = new Date();
                    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
                    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
                    onStartDateChange(firstDay.toISOString().split('T')[0]);
                    onEndDateChange(lastDay.toISOString().split('T')[0]);
                  }}
                  className="px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Mês Atual
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const now = new Date();
                    const firstDay = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                    const lastDay = new Date(now.getFullYear(), now.getMonth(), 0);
                    onStartDateChange(firstDay.toISOString().split('T')[0]);
                    onEndDateChange(lastDay.toISOString().split('T')[0]);
                  }}
                  className="px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Mês Anterior
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const now = new Date();
                    const day15LastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 15);
                    const day14ThisMonth = new Date(now.getFullYear(), now.getMonth(), 14);
                    onStartDateChange(day15LastMonth.toISOString().split('T')[0]);
                    onEndDateChange(day14ThisMonth.toISOString().split('T')[0]);
                  }}
                  className="px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  15 do Mês Anterior → 14 do Mês Atual
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
